import {
  PAYMENT_METHOD_NAMES,
  type SparkOptions,
  STANDARD_CHECKOUT_ELEMENTS,
  type StandardCheckoutElement,
} from './types/spark-options';

const DEFAULT_HIDDEN_STANDARD_ELEMENTS = new Set<StandardCheckoutElement>([
  'po-number',
  'shipping-requested-date',
]);

export const DEFAULT_SPARK_OPTIONS: SparkOptions = {
  translations: [],
  showTranslations: false,
  display: {
    darkTheme: false,
    savingsUseRrp: false,
    showRrpToggle: false,
    showPriceBreakSavings: false,
    roundPriceBreakPercentages: false,
    hideFinancials: false,
    orderListShowShippingAddress: false,
    sectionConfiguration: { 'my-account': { order: [], hide: [] } },
    showSlots: [],
  },
  paymentMethodsOrder: [...PAYMENT_METHOD_NAMES],
  saveToAddressBookDefault: true,
  checkoutCustomElements: STANDARD_CHECKOUT_ELEMENTS.map((id) => ({
    kind: 'standard' as const,
    id,
    hidden: DEFAULT_HIDDEN_STANDARD_ELEMENTS.has(id),
    required: false,
  })),
  analytics: null,
};

const LANGUAGE_OPTIONS = [
  { value: 'ar', label: 'Arabic (ar)' },
  { value: 'bg', label: 'Bulgarian (bg)' },
  { value: 'cs', label: 'Czech (cs)' },
  { value: 'da', label: 'Danish (da)' },
  { value: 'de', label: 'German (de)' },
  { value: 'el', label: 'Greek (el)' },
  { value: 'en', label: 'English (en)' },
  { value: 'es', label: 'Spanish (es)' },
  { value: 'fi', label: 'Finnish (fi)' },
  { value: 'fr', label: 'French (fr)' },
  { value: 'ge', label: 'Georgian (ge)' },
  { value: 'he', label: 'Hebrew (he)' },
  { value: 'hr', label: 'Croatian (hr)' },
  { value: 'hu', label: 'Hungarian (hu)' },
  { value: 'is', label: 'Icelandic (is)' },
  { value: 'it', label: 'Italian (it)' },
  { value: 'ja', label: 'Japanese (ja)' },
  { value: 'lt', label: 'Lithuanian (lt)' },
  { value: 'lv', label: 'Latvian (lv)' },
  { value: 'nb', label: 'Norwegian Bokmal (nb)' },
  { value: 'nl', label: 'Dutch (nl)' },
  { value: 'no', label: 'Norwegian (no, defaults to nb)' },
  { value: 'pl', label: 'Polish (pl)' },
  { value: 'pt-br', label: 'Portuguese, Brazil (pt-br)' },
  { value: 'pt', label: 'Portuguese (pt)' },
  { value: 'ro', label: 'Romanian (ro)' },
  { value: 'ru', label: 'Russian (ru)' },
  { value: 'sk', label: 'Slovak (sk)' },
  { value: 'sl', label: 'Slovenian (sl)' },
  { value: 'sv', label: 'Swedish (sv)' },
  { value: 'th', label: 'Thai (th)' },
  { value: 'zh', label: 'Chinese (zh)' },
] as const;

/** Mutable copy of {@link LANGUAGE_OPTIONS} for components that take a plain options array. */
export const LANGUAGE_CODE_OPTIONS = [...LANGUAGE_OPTIONS];

export const TEMPLATE_ID_OPTIONS = [
  { value: 'spark-slot-my-account', label: 'My Account' },
  { value: 'spark-slot-my-cart', label: 'My Cart' },
  { value: 'spark-slot-my-cart-line-item', label: 'Cart line item' },
  { value: 'spark-slot-order-complete', label: 'Order complete' },
  { value: 'spark-slot-cart-shipping-step', label: 'Cart shipping step' },
  { value: 'spark-slot-cart-payment-step', label: 'Cart payment step' },
  { value: 'spark-slot-purchase-summary-header', label: 'Purchase summary header' },
  { value: 'spark-slot-purchase-summary-footer', label: 'Purchase summary footer' },
] as const;
export type TemplateId = (typeof TEMPLATE_ID_OPTIONS)[number]['value'];

const DOCS_BASE = 'https://docs.sparklayer.io';

export const DOCS_URLS = {
  pricing: {
    darkTheme: `${DOCS_BASE}/customising-design#dark-mode`,
    pricingDisplay: `${DOCS_BASE}/pricing-display`,
    showRrpToggle: `${DOCS_BASE}/pricing-display#show-rrp-prices-only-for-in-store-shopping`,
    applyPriceBreaksAcrossVariants: `${DOCS_BASE}/dashboard-configurations#J5YMy`,
  },
  checkout: {
    checkoutElements: `${DOCS_BASE}/cart-and-checkout`,
    paymentMethodsOrder: `${DOCS_BASE}/payment-methods`,
    saveToAddressBook: `${DOCS_BASE}/account-and-addresses`,
    cartSearchType: `${DOCS_BASE}/my-order-interface#FABUR`,
    hideAICartFeatures: `${DOCS_BASE}/intelligent-cart`,
  },
  myAccount: {
    accountSections: `${DOCS_BASE}/my-account-interface`,
    hideFinancials: `${DOCS_BASE}/my-account-interface#account-information`,
    orderListShippingAddress: `${DOCS_BASE}/account-and-addresses#show-shipping-address-summary`,
    paymentVisibility: `${DOCS_BASE}/payment-methods#payments-visibility`,
    purchaseTimeline: `${DOCS_BASE}/timeline`,
  },
  translationOverrides: `${DOCS_BASE}/languages-and-international`,
  showTranslations: `${DOCS_BASE}/languages-and-international#text-and-language-overwrites`,
  slots: `${DOCS_BASE}/custom-slots`,
  analytics: `${DOCS_BASE}/tech-docs/javascript-sdk#integrating-google-analytics-with-sparklayer-sdk`,
} as const;
